import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAtsCallFromList } from '@/api/atsApi';
import { ATS_CALL_FROM_LOAD_ERROR_MESSAGE } from '@/constants/atsCalls';
import { type IAtsCallFromResponse } from '@/types/IAtsCallFromResponse';
import { getErrorData, type IErrorData } from '@/utils/getErrorData';
import { hasResponse } from '@/utils/typeguards/hasResponse';
import { type RootState } from '../../store';

interface IFetchAtsCallFromListParams {
  // Принудительный перезапрос — для кнопки «Повторить» после ошибки
  force?: boolean;
}

/**
 * Thunk загрузки списка линий, с которых менеджер может позвонить.
 * Запрос уходит один раз за сессию: повторные вызовы отсекает `condition`,
 * поэтому десятки кнопок звонка на странице не устраивают шторм запросов.
 */
export const fetchAtsCallFromListThunk = createAsyncThunk<
  string[],
  IFetchAtsCallFromListParams | undefined,
  { rejectValue: IErrorData; state: RootState }
>(
  'atsCallFrom/fetchList',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getAtsCallFromList();

      if (!hasResponse<IAtsCallFromResponse>(data)) {
        return rejectWithValue({ message: ATS_CALL_FROM_LOAD_ERROR_MESSAGE });
      }

      const { items } = data.response;

      if (!Array.isArray(items)) {
        return rejectWithValue({ message: ATS_CALL_FROM_LOAD_ERROR_MESSAGE });
      }

      return items.filter(
        (item) => typeof item === 'string' && item.trim() !== ''
      );
    } catch (error) {
      return rejectWithValue(getErrorData(error));
    }
  },
  {
    condition: (params, { getState }) => {
      const { isLoading, hasLoaded } = getState().atsCallFrom;

      if (isLoading) {
        return false;
      }

      return params?.force === true || !hasLoaded;
    }
  }
);
